import {
  LOCK_CATEGORY,
  UNLOCK_CATEGORY,
  RETAIN_LOCK_CATEGORY,
} from '../apollo/mutations/categoryLock.web.graphql';
import { useMutation } from '@apollo/client';

/**
 * Custom hook for managing concurrential access to categories
 * @returns Object with methods for locking, unlocking, and retaining category locks
 */
export const useCategoryConcurrentialAccess = () => {
  const [lockCategoryMutation] = useMutation(LOCK_CATEGORY);
  const [unlockCategoryMutation] = useMutation(UNLOCK_CATEGORY);
  const [retainLockCategoryMutation] = useMutation(RETAIN_LOCK_CATEGORY);

  /**
   * Locks a category for editing by the current user
   * @param categoryId The ID of the category to lock
   * @returns A promise that resolves when the lock is applied
   */
  const lockCategory = async (categoryId: string): Promise<void> => {
    await lockCategoryMutation({
      variables: {
        where: { id: categoryId },
      },
    });
  };

  /**
   * Unlocks a category, releasing it for other users to edit
   * @param categoryId The ID of the category to unlock
   * @returns A promise that resolves when the unlock is applied
   */
  const unlockCategory = async (categoryId: string): Promise<void> => {
    await unlockCategoryMutation({
      variables: {
        where: { id: categoryId },
      },
    });
  };

  /**
   * Retains the lock on a category for the current user
   * @param categoryId The ID of the category to retain the lock for
   * @returns A promise that resolves when the lock is retained
   */
  const retainCategoryLock = async (categoryId: string): Promise<void> => {
    await retainLockCategoryMutation({
      variables: {
        where: { id: categoryId },
      },
    });
  };

  return {
    lockCategory,
    unlockCategory,
    retainCategoryLock,
  };
};
